import usePaginate from "@/components/paginate/usePaginate";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobDefinition } from "@/helpers/apis";
import { getDateByEndOfDayWithoutTimeoffset, getDateByStartOfDayWithoutTimeoffset } from "@/components/dateRange/utils";
import { RelativeComponentLoader } from "@/components/loader";
import { Stack } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import dayjs from "dayjs";
import { DataTable } from "@/components/tables";
import { SnowflakeJobDefinition } from "@/pages/query/types";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { Paginate } from "@/components/paginate";

export const CustomWorkloadDefinition = ({ workflowId }: { workflowId: string }) => {
  const { currency } = useAppState();
  const { page, pageSize, setPage, updatePageSize } = usePaginate();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ["snowflake_jobs", "definition", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobDefinition(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
  });
  if (isLoading) {
    return <RelativeComponentLoader label="Loading" />;
  }
  return (
    <Stack direction="column">
      <Stack>
        <div className="fs-4">Latest Job Definition</div>
        <div className="spacer" />
        <DateRange
          startDate={startDate}
          endDate={endDate}
          availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
          disableCalendarSelection
          onDateRangeSelect={onUpdate}
        />
      </Stack>
      <Stack>
        <Stack gap="xxs">
          <div className="al-text-light-gray">Run ID:</div>
          <div className="al-text-brand">{data?.run_id}</div>
        </Stack>
        <Stack gap="xxs">
          <div className="al-text-light-gray">Latest timestamp:</div>
          <div className="al-text-brand">
            {dayjs(data?.timestamp).format("DD MMM YYYY hh:mm:ss A")}
          </div>
        </Stack>
      </Stack>
      <DataTable
        header={[
          {
            id: 1,
            label: "Task ID",
            uid: "task_id",
            render: (item: SnowflakeJobDefinition) => (
              <div className="al-text-brand al-break-all">{item.task_id}</div>
            ),
          },
          {
            id: 2,
            label: "Avg. Cost",
            uid: "query_cost",
            render: (item: SnowflakeJobDefinition) => (
              <div>{formatCost(item.query_cost, currency)}</div>
            ),
          },
          {
            id: 3,
            label: "Avg. Time",
            uid: "execution_time",
            render: (item: SnowflakeJobDefinition) => (
              <div>{formatMilliseconds(item.execution_time)}</div>
            ),
          },
          {
            id: 4,
            label: "Runs",
            uid: "run_count",
            render: (item: SnowflakeJobDefinition) => <div>{item.run_count}</div>,
          },
          {
            id: 5,
            label: "Status",
            uid: "status",
            render: (item: SnowflakeJobDefinition) => <ExecutionStatusCount {...item} />,
          },
        ]}
        data={{
          items: (data?.definition ?? []).slice(page * pageSize, (page + 1) * pageSize),
        }}
        idKey={(item: SnowflakeJobDefinition) => item.task_id}
        gridTemplateColumns="3fr 1fr 1fr 0.8fr 1fr"
      />
      <Paginate
        itemCount={data?.definition.length || 0}
        numPages={Math.ceil((data?.definition.length || 0) / pageSize)}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};